import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { 
  RefreshCw, 
  Check, 
  X, 
  Clock,
  AlertCircle,
  CheckCircle2,
  Calendar,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { 
  usePaymentAgreements, 
  usePaymentAgreementsByDeal,
  useAgreementPaymentSummary,
  useApproveRenegotiation,
  useRejectRenegotiation,
  getAgreementReasonLabel,
  type PaymentAgreement 
} from '@/hooks/finance/usePaymentAgreements';
import { OriginalAgreementDetails } from './approval/OriginalAgreementDetails';
import { NewProposalDetails } from './approval/NewProposalDetails';
import { PreviousRenegotiationCard } from './approval/PreviousRenegotiationCard';

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
}

interface PendingCardProps {
  agreement: PaymentAgreement;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  isApproving: boolean;
}

function PendingAgreementCard({ agreement, onApprove, onReject, isApproving }: PendingCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Fetch all agreements for this deal
  const { data: allAgreements = [] } = usePaymentAgreementsByDeal(agreement.deal_id);
  
  // Find the previous agreement (being replaced)
  const previousAgreement = allAgreements.find(a => 
    a.status === 'active' || a.agreement_number === agreement.agreement_number - 1
  );
  
  // Get summary of previous agreement
  const { data: previousSummary, isLoading: isLoadingSummary } = useAgreementPaymentSummary(
    previousAgreement?.id || null
  );
  
  // Previous renegotiations history (excluding current pending)
  const previousRenegotiations = allAgreements.filter(a => 
    a.agreement_number > 1 && 
    a.agreement_number < agreement.agreement_number &&
    a.status !== 'pending_approval'
  );

  const installmentAmount = agreement.num_installments > 0 
    ? agreement.agreed_amount / agreement.num_installments 
    : 0;

  const hasCustomInstallments = agreement.custom_installments && agreement.custom_installments.length > 0;

  const renegotiationLabel = `${agreement.agreement_number - 1}ª Renegociação`;

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <RefreshCw className="h-4 w-4 text-purple-600" />
              <span className="font-medium">{agreement.client_name || 'Cliente'}</span>
              <Badge variant="secondary" className="text-xs">
                <Clock className="h-3 w-3 mr-1" />
                Aguardando
              </Badge>
              <Badge 
                variant="outline" 
                className="text-xs bg-amber-50 text-amber-700 border-amber-200"
              >
                {renegotiationLabel}
              </Badge>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm mb-3">
              <div>
                <p className="text-muted-foreground">Saldo Original</p>
                <p className="font-medium">{formatCurrency(agreement.original_balance)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Nova Proposta</p>
                <p className="font-medium text-primary">
                  {hasCustomInstallments 
                    ? `${agreement.num_installments}x (valores customizados)`
                    : `${agreement.num_installments}x ${formatCurrency(installmentAmount)}`
                  }
                </p>
                <p className="text-xs text-muted-foreground">
                  Total: {formatCurrency(agreement.agreed_amount)}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4 text-sm flex-wrap">
              {agreement.reason && (
                <Badge variant="outline">
                  {getAgreementReasonLabel(agreement.reason)}
                </Badge>
              )}
              {agreement.first_due_date && (
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Calendar className="h-3 w-3" />
                  1ª parcela: {format(parseISO(agreement.first_due_date), "dd/MM/yyyy", { locale: ptBR })}
                </span>
              )}
              <span className="text-muted-foreground">
                {format(parseISO(agreement.created_at), "dd/MM/yyyy", { locale: ptBR })}
              </span>
            </div>

            {agreement.requester_name && (
              <p className="text-xs text-muted-foreground mt-2">
                Solicitado por: {agreement.requester_name}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Button 
              size="sm" 
              variant="default"
              className="bg-green-600 hover:bg-green-700"
              onClick={() => onApprove(agreement.id)}
              disabled={isApproving}
            >
              <Check className="h-4 w-4 mr-1" />
              {isApproving ? 'Aprovando...' : 'Aprovar'}
            </Button>
            <Button 
              size="sm" 
              variant="destructive"
              onClick={() => onReject(agreement.id)}
            >
              <X className="h-4 w-4 mr-1" />
              Rejeitar
            </Button>
          </div>
        </div>

        {/* Expandable Details */}
        <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="w-full mt-3">
              {isExpanded ? (
                <>
                  <ChevronUp className="h-4 w-4 mr-1" />
                  Ocultar detalhes
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4 mr-1" />
                  Ver detalhes
                </>
              )}
            </Button>
          </CollapsibleTrigger>
          
          <CollapsibleContent className="pt-4 space-y-4">
            {/* Previous Renegotiations History (if any) */}
            {previousRenegotiations.length > 0 && (
              <div className="space-y-3">
                <p className="text-xs font-medium uppercase text-amber-600 flex items-center gap-1">
                  <AlertCircle className="h-3.5 w-3.5" />
                  Renegociações Anteriores
                </p>
                {previousRenegotiations.map(reneg => (
                  <PreviousRenegotiationCard key={reneg.id} agreement={reneg} />
                ))}
              </div>
            )}
            
            {/* Current Active Agreement Details */}
            <OriginalAgreementDetails 
              agreement={previousAgreement}
              summary={previousSummary}
              isLoading={isLoadingSummary}
            />
            
            {/* New Proposal Details */}
            <NewProposalDetails agreement={agreement} />
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
}

export function RenegotiationApprovalQueue() {
  const { data: pendingAgreements, isLoading } = usePaymentAgreements('pending_approval');
  const approveMutation = useApproveRenegotiation();
  const rejectMutation = useRejectRenegotiation();
  
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvingId, setApprovingId] = useState<string | null>(null);

  const handleApprove = async (id: string) => {
    setApprovingId(id);
    try {
      await approveMutation.mutateAsync(id);
    } finally {
      setApprovingId(null);
    }
  };

  const handleReject = async () => {
    if (!rejectingId) return;
    await rejectMutation.mutateAsync({ 
      agreementId: rejectingId, 
      rejectionReason 
    });
    setRejectingId(null);
    setRejectionReason('');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const hasPending = pendingAgreements && pendingAgreements.length > 0;

  if (!hasPending) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-500 opacity-70" />
          Nenhuma renegociação pendente de aprovação.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-amber-500" />
          <h3 className="font-medium">
            {pendingAgreements.length} renegociação(ões) aguardando aprovação
          </h3>
        </div>

        <div className="space-y-3">
          {pendingAgreements.map(agreement => (
            <PendingAgreementCard
              key={agreement.id}
              agreement={agreement}
              onApprove={handleApprove}
              onReject={setRejectingId}
              isApproving={approvingId === agreement.id}
            />
          ))}
        </div>
      </div>

      {/* Rejection Dialog */}
      <Dialog open={!!rejectingId} onOpenChange={(open) => !open && setRejectingId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejeitar Renegociação</DialogTitle>
            <DialogDescription>
              Informe o motivo da rejeição para o solicitante.
            </DialogDescription>
          </DialogHeader>

          <Textarea
            placeholder="Motivo da rejeição..."
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            rows={3}
          />

          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectingId(null)}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleReject}
              disabled={rejectMutation.isPending || !rejectionReason.trim()}
            >
              {rejectMutation.isPending ? 'Rejeitando...' : 'Confirmar Rejeição'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
